/**
 * Phase 286 — Forecasting tracker page.
 *
 * Per-consultant vacancy list with est. values + est. placed months, plus the
 * monthly forecast rollup. Reads GET /internal/v0/reporting/forecasting via
 * the bearer fetcher.
 *
 * Renders as-of freshness disclosure (ADR-068 D3 / ADR-074 D4).
 * Empty-state until Phase 292 (#955) writes forecasting facts to the store.
 * When asOf is null (no forecasting facts yet), shows "no forecasting data yet"
 * rather than a blank LastUpdated.
 *
 * Wave 8 (the 2026-08-17 Sign-Off bounce remediation):
 *   1. the sub-nav is the shared <ReportingTabs> strip both reporting screens mount;
 *   2. currency metric columns render £-formatted (21000 → £21,000);
 *   3. the rollup defaults to current-month-forward (Europe/London) with a
 *      "Show earlier months" toggle, so a year of stale months no longer leads
 *      the table.
 * Presentation stays deliberately plain — the BI-grade portal redesign is #1160/#1163.
 *
 * Design tokens only — hex-fence.test.ts POLISH-01.
 */
import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  LastUpdated,
  NavRail,
  PageHeader,
} from "@ardept/design-system";
import { useForecastingRollup } from "../api/reportingQueries.js";
import { useMe } from "../api/queries.js";
import type { ForecastingCell } from "../api/types.js";
import { ReportingTabs } from "./ReportingTabs.js";
import "./ForecastingScreen.css";
import "./screens.css";

// ── Types ───────────────────────────────────────────────────────────────────

/** One pivot row in the monthly-rollup display (one month across all metric keys). */
export interface RollupRow {
  month: string;
  /** Values keyed by metricKey; zero for missing combinations. */
  values: Record<string, number>;
}

// ── Pure data transformation ─────────────────────────────────────────────────

/**
 * Derives the sorted unique metric keys from the cells (dynamic vocabulary —
 * Phase 292 / #955 defines the forecasting metric keys).
 */
export function deriveMetricKeys(cells: ForecastingCell[]): string[] {
  const seen = new Set<string>();
  for (const c of cells) seen.add(c.metric_key);
  return [...seen].sort();
}

/**
 * Pure transformation: pivots ForecastingCell[] into per-month rollup rows,
 * each with values keyed by metricKey.
 *
 * Exported for unit-testability (no React; no fetch; no side effects).
 * The integration test (test/integration/forecasting.test.ts) uses a mirror of
 * this function to verify the contract without importing React types.
 */
export function buildForecastingRollup(cells: ForecastingCell[]): RollupRow[] {
  const lookup = new Map<string, RollupRow>();
  const metricKeys = deriveMetricKeys(cells);

  for (const cell of cells) {
    if (!lookup.has(cell.month)) {
      const emptyValues: Record<string, number> = {};
      for (const m of metricKeys) emptyValues[m] = 0;
      lookup.set(cell.month, { month: cell.month, values: emptyValues });
    }
    const row = lookup.get(cell.month)!;
    row.values[cell.metric_key] = (row.values[cell.metric_key] ?? 0) + cell.value;
  }

  // Sort months ascending (earliest first — chronological order for a forecast).
  return [...lookup.values()].sort((a, b) => a.month.localeCompare(b.month));
}

// ── Currency presentation ────────────────────────────────────────────────────

/**
 * ONE module-level formatter instance — Intl.NumberFormat construction is the
 * expensive part, and the rollup renders it once per currency cell per paint.
 *
 * GBP is a DISPLAY-LAYER choice of the operator console for the UK pilot: the
 * reporting fact store carries no currency dimension (ADR-074 facts are bare
 * whole-unit numbers — `Σ est_value whole-£` / `Σ fee whole-£`, phase 292), so
 * there is nothing on the wire to switch on. If a non-GBP tenant ever lands, the
 * currency must become a tenant/fact dimension first — do NOT infer it here.
 *
 * maximumFractionDigits: 0 — the facts are whole pounds; pence would be noise.
 */
const GBP_FORMATTER = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

/**
 * True for metric keys whose values are money.
 *
 * The live forecasting vocabulary is `forecast_value` (family sales-forecast) and
 * `revenue` (family revenue-actuals) — phase 292 — and phase 338 / #1161 adds
 * `forecast_value_priority`, which this pattern already covers. Deliberately a
 * pattern rather than an allow-list: the rollup's metric columns are derived from
 * whatever keys the fact store returns (deriveMetricKeys), so a new money key must
 * format correctly the day it is written, not the day someone remembers this file.
 *
 * Never true for the nine locked weekly count keys (cltvs, cints, spec_cvs,
 * submissions, interviews, forwarding_interviews, notes, new_vacancies,
 * new_contacts) — counts must never render as money. NB `forecast_job_count`
 * likewise stays a count.
 *
 * Exported for unit-testability (no React; no side effects).
 */
export function isCurrencyMetric(key: string): boolean {
  return /(value|revenue)/.test(key);
}

/** Formats a whole-pound amount for display (21000 → "£21,000"). Exported for tests. */
export function formatCurrencyGbp(value: number): string {
  return GBP_FORMATTER.format(value);
}

/**
 * Renders one rollup cell: currency keys as £, everything else as the plain count
 * it already was (bounce finding 3a is a formatting fix, not a data change).
 */
export function formatMetricValue(metricKey: string, value: number): string {
  return isCurrencyMetric(metricKey) ? formatCurrencyGbp(value) : String(value);
}

// ── Month-window presentation ────────────────────────────────────────────────

/**
 * The current `yyyy-MM` in Europe/London — the tenant's reporting timezone, which
 * is what the fact store's month buckets are keyed on (phase 292 windows).
 *
 * Explicit timeZone, NOT the browser default: the pilot is UK but an operator may
 * open the console from anywhere, and a machine an hour either side of London must
 * not silently see a different "current month".
 *
 * Exported (with an injectable clock) so the node-only harness can pin it.
 */
export function currentMonthLondon(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(now);
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  return `${year}-${month}`;
}

/**
 * Applies the current-month-forward default to built rollup rows (bounce finding
 * 3b: a year of stale 2025-08..2026-07 rows of a dead metric led the table).
 *
 * `showAll` (the "Show earlier months" toggle) returns the full history untouched.
 *
 * GUARD — if every row is in the past, the forward filter would hide ALL of them
 * and the screen would render a false "no data" table. That is exactly the
 * dead-metric shape Carlos hit, so in that case the full history is returned
 * instead: showing stale months is honest, showing nothing is a lie.
 *
 * Pure: `yyyy-MM` strings compare lexicographically, so no Date parsing is needed.
 * Exported for unit-testability (no React; no side effects).
 */
export function visibleRollupRows(
  rows: readonly RollupRow[],
  currentMonth: string,
  showAll: boolean,
): RollupRow[] {
  if (showAll) return [...rows];
  const forward = rows.filter((r) => r.month >= currentMonth);
  if (forward.length === 0 && rows.length > 0) return [...rows];
  return forward;
}

// ── Screen component ─────────────────────────────────────────────────────────

export function ForecastingScreen(): JSX.Element {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");

  // Auto-select first allowed tenant once /me resolves.
  useEffect(() => {
    if (me?.allowed_tenants.length && !selectedTenantId) {
      setSelectedTenantId(me.allowed_tenants[0]?.id ?? "");
    }
  }, [me, selectedTenantId]);

  const { data, isLoading, error } = useForecastingRollup({
    tenantId: selectedTenantId,
  });

  const metricKeys = useMemo(
    () => deriveMetricKeys(data?.cells ?? []),
    [data],
  );

  const rollupRows = useMemo(
    () => buildForecastingRollup(data?.cells ?? []),
    [data],
  );

  // Month window: current-month-forward by default; the toggle reveals history.
  const [showEarlierMonths, setShowEarlierMonths] = useState(false);
  const currentMonth = useMemo(() => currentMonthLondon(), []);
  const visibleRows = useMemo(
    () => visibleRollupRows(rollupRows, currentMonth, showEarlierMonths),
    [rollupRows, currentMonth, showEarlierMonths],
  );
  const earlierCount = rollupRows.filter((r) => r.month < currentMonth).length;
  // No toggle when there is nothing to reveal, and none when EVERY month is past —
  // the visibleRollupRows guard already shows them all, so a toggle would be a no-op.
  const canToggleMonths = earlierCount > 0 && earlierCount < rollupRows.length;

  const hasData = rollupRows.length > 0;
  const isEmpty = !isLoading && !error && data && data.cells.length === 0;
  // as_of is null when no forecasting facts have been written yet (#955 not landed).
  const asOfNull = data !== undefined && data.as_of === null;

  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  const tenantOptions = me?.allowed_tenants ?? [];

  return (
    <div className="app">
      <NavRail active="reporting" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Forecasting"
          subtitle="Monthly forecast rollup. Sourced from the reporting fact store (ADR-074). Populated by the forecasting fact run."
        />

        {/* D-FETCH-01 freshness indicator — last successful React Query fetch.
            When asOf is null (no forecasting facts yet), show a descriptive state
            rather than a blank or misleading timestamp. */}
        {asOfNull ? (
          <div className="fc-no-data" role="status">
            No forecasting data yet — facts will appear here once the first forecasting
            report run completes (Phase 292, forecasting fact run).
          </div>
        ) : (
          data?.as_of && <LastUpdated timestamp={new Date(data.as_of)} />
        )}

        {/* Reporting sub-nav — the shared strip, mounted in the same position on
            both reporting screens so it does not move between the two pages. */}
        <ReportingTabs active="forecasting" />

        {/* Tenant selector — shown when operator has access to multiple tenants */}
        {tenantOptions.length > 1 && (
          <div className="fc-filter">
            <label className="fc-filter__label" htmlFor="fc-tenant-select">
              Tenant
            </label>
            <select
              id="fc-tenant-select"
              className="fc-filter__select"
              value={selectedTenantId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedTenantId(e.target.value)
              }
            >
              {tenantOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.slug}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Loading state */}
        {isLoading && !data && (
          <div className="fc-loading">Loading forecasting data...</div>
        )}

        {/* Error state */}
        {error && (
          <div className="fc-error" role="status">
            Could not load forecasting data (engine unreachable). Retrying automatically.
          </div>
        )}

        {/* No tenant access */}
        {!isLoading && !error && me && me.allowed_tenants.length === 0 && (
          <EmptyState
            iconName="octagon-alert"
            title="No tenant access"
            body="Ask an administrator to grant you access to a tenant."
          />
        )}

        {/* Empty fact store — no data yet */}
        {isEmpty && !hasData && (
          <EmptyState
            iconName="bar-chart-2"
            title="No forecasting data yet"
            body="Monthly forecasting facts will appear here once the first forecasting report run completes."
          />
        )}

        {/* Monthly rollup table — shown when data is loaded and non-empty */}
        {!isLoading && !error && hasData && (
          <div className="fc-section">
            <div className="fc-section__bar">
              <div className="fc-section__head">Monthly Forecast Rollup</div>
              {canToggleMonths && (
                <button
                  type="button"
                  className="fc-toggle"
                  aria-pressed={showEarlierMonths}
                  onClick={() => setShowEarlierMonths((v) => !v)}
                >
                  {showEarlierMonths
                    ? "Hide earlier months"
                    : `Show earlier months (${earlierCount})`}
                </button>
              )}
            </div>
            <div className="fc-rollup-wrap">
              <table className="fc-rollup" aria-label="Monthly forecasting rollup">
                <thead>
                  <tr>
                    <th className="fc-th fc-th--month">Month</th>
                    {metricKeys.map((m) => (
                      <th key={m} className="fc-th fc-th--metric" title={m}>
                        {m.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.month} className="fc-row">
                      <td className="fc-td fc-td--month">{row.month}</td>
                      {metricKeys.map((m) => (
                        <td key={m} className="fc-td fc-td--metric">
                          {formatMetricValue(m, row.values[m] ?? 0)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ForecastingScreen;
